// threads.mjs — 组间通信页的纯逻辑（第一百零四批 B）。无 React、无 DOM。
// 房屋风格同 pages/reports.mjs：可判定的东西离开 JSX 才有判据。
//
// 这页数据的三个特殊之处，决定了下面每只函数的形状：
//
// 1) **全员全量可读**（需求方裁的透明口径）：列表不按 currentGroup 过滤，所以
//    这里没有 noGroup 形态——"没有当前组"在本页不是一个要渲染的状态。
// 2) 写入受 canLiaison 门控，且回帖的主体是**组**不是人：asGroup 必须在线的
//    **参与集**里。哪个组是默认、什么时候锁死，是可判定规则（defaultAsGroup），
//    不该散在组件里。
// 3) 消息流要求 **seq 升序**。后端合同说的就是 asc，但这条**显示语义**需要一个
//    本地判据（normalizeThreadDetail 显式排一次）——否则它唯一的依据是远端一句
//    ORDER BY，谁改了那句都没有任何东西会响（reports.mjs 头注 3 同一笔账）。
//
// ── 第一百零六批 C1：两端 → 参与组集合 ─────────────────────────────────────
// 线不再是双边：合同新增 `parties`（slug 数组，顺序＝加入先后，第 0 项是发起组），
// aGroup/bGroup 降级为"发起两方"的历史字段。本模块一律读 parties——**除了**
// normalizeThreads 那一处兼容回落：老后端（或回滚后的镜像）不带 parties 时，
// 用 aGroup/bGroup 拼出等价的两元集合。回落写在收敛点这一处，页面无从感知。

import { groupLabel } from '../api/groups.mjs';

// ── 第一百二十五批 C：归档 + 三日否决窗口（需求方 2026-08-20 三裁）──────────────
// 「①任意参与方可发起归档；②三日内任意参与方可取消，到期无异议则自动归档；
//  ③归档＝只读 + 默认列表隐藏 + 可重开；④窗口内任一方发新言即自动取消。」
// 本模块只做三件：把服务端的 `archiveState` 收成受控值、按它算三个按钮的显隐、
// 以及**不做的那件事**——不重算状态（后端 thread_state 是唯一判定处，理由见
// normalizeThreads 里那段注释）。
// 归档态与 `status`（open|closed）是**正交的两维**：一条 closed 的线照样可以归档，
// 一条归档的线重开之后仍然是 closed。两者混成一个字段的下场是"重开"要先猜它原来
// 是不是 closed。
export const ARCHIVE_STATES = ['open', 'archive_pending', 'archived'];

// 组引用 → 受控形状。实际后端（routes_threads.cpp 的 slug_of）给的是**裸 slug
// 字符串**；对象形状（id/slug/双语名，/api/me 组行同构）也收——万一后端哪天把
// 名字随线带出来，这里不用改调用侧。取名一律走 groups.mjs 的 groupLabel。
function normalizeGroupRef(g) {
  if (typeof g === 'string') {
    const slug = g.trim();
    return slug ? { id: null, slug, displayName: '', displayNameEn: '' } : null;
  }
  if (!g || typeof g !== 'object') return null;
  const slug = typeof g.slug === 'string' ? g.slug.trim() : '';
  if (!slug && g.id == null) return null;      // 连身份都没有的端点不可用
  return {
    id: g.id ?? null,
    slug,
    displayName: typeof g.displayName === 'string' ? g.displayName : '',
    displayNameEn: typeof g.displayNameEn === 'string' ? g.displayNameEn : '',
  };
}

// ── 回落必须出声（第一百零六批 R2）──────────────────────────────────────────
// 一条**零参与组**的线只有两种成因：后端老（不带 parties 字段），或者参与集表的
// 回填迁移漏了这条线。前者是暂态、后者是故障，而两者在页面上原本长得一模一样——
// 静默回落成 a/b 两端之后，那条线看起来完全正常：徽标有两枚、回帖框照开。
// 于是"迁移漏了一条线"这件事**永远不会有人发现**，直到某个第三方发现自己回不了帖。
// 所以回落照做（读面不能因此瞎），但要留下两处痕迹：行上带 partiesInferred=true
// 给界面挂一枚"参与组待补"，外加一次性的 console 告警（同一次会话只吼一声——
// 每行吼一次会把控制台刷成噪音，而噪音与沉默的效果是一样的）。
let inferredWarned = false;

// 只给单测用的复位口：这条一次性状态是**模块级**的，同一个进程里所有用例共享它，
// 不给复位口的话"只吼一声"这条判据本身就测不到（第一个碰到回落的用例会把它烧掉，
// 后面的用例只能断言"≤1 次"——而 0 次也满足 ≤1，那种断言等于没有）。
export function resetInferredPartiesWarning() { inferredWarned = false; }

function warnInferred(id) {
  if (inferredWarned) return;
  inferredWarned = true;
  // eslint-disable-next-line no-console
  console.warn(`[threads] 线 #${id} 的 parties 缺席，参与组是按 aGroup/bGroup 两端**推得**的。`
    + '若后端已是本批版本，说明 group_thread_party 的回填迁移漏了这条线——'
    + '这一枚告警就是查它的起点（本次会话只报一次）。');
}

// 线行 → 参与组集合（受控形状、按 slug 去重、保持后端给的加入先后）。
// 合同的权威字段是 `parties`；它缺席时（老后端、回滚镜像、或漏迁移的线）用
// aGroup/bGroup 拼一个等价的两元集合——**回落只此一处**，不散进每个读它的地方，
// 否则"三方线在某个角落被当成两方"这种错会以每处一份的速度繁殖。
// 返回 { parties, inferred }：inferred 就是上面那句"这不是后端说的，是我们猜的"。
function normalizeParties(row) {
  const given = Array.isArray(row?.parties) && row.parties.length ? row.parties : null;
  const src = given ?? [row?.aGroup, row?.bGroup];
  const out = [];
  const seen = new Set();
  for (const g of src) {
    const n = normalizeGroupRef(g);
    if (!n || !n.slug || seen.has(n.slug)) continue;
    seen.add(n.slug);
    out.push(n);
  }
  // 两端也拼不出任何组时**不算推得**：那是一条什么都没有的线（调用侧不渲染徽标），
  // 挂一枚"按两方推得"只会指向一个不存在的推断。
  const inferred = given === null && out.length > 0;
  if (inferred) warnInferred(row?.id);
  return { parties: out, inferred };
}

// 列表行 → 受控形状。缺 id 的行是哑弹（点开取不到详情），进渲染前滤掉。
// id 一律收成字符串：后端给整数、URL 里是字符串、选中态攥在组件手里也是字符串，
// 三处混用 ===/!== 时数字与字符串的不等是这类页面最经典的静默失配。
// 刻意**不排序**：列表顺序是后端的展示决定（合同带 lastAtMs，最近活跃在前是
// 它的语义），前端再排一次就是第二个会漂的顺序。
export function normalizeThreads(data) {
  const rows = Array.isArray(data?.threads) ? data.threads : [];
  const out = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object' || r.id == null) continue;
    const id = String(r.id);
    const p = normalizeParties(r);
    out.push({
      id,
      // 主题缺席回落 #id：一条没有主题的线仍要能被点开，空行等于把它藏起来。
      subject: typeof r.subject === 'string' && r.subject.trim() ? r.subject.trim() : `#${id}`,
      // 参与组集合：合同的权威字段。**唯一**的兼容回落写在这里——后端不带
      // parties（老镜像/回滚/漏迁移）时用 aGroup/bGroup 拼两元集合。
      // 按 slug 去重、剔掉不成形的项：一枚重复徽标是噪音，一枚空徽标是谎言。
      parties: p.parties,
      // 这一行的参与集是**推得的**而不是后端给的（normalizeParties 头注）。
      // 页面据它挂一枚"参与组待补"——静默回落会让漏迁移的线永远无人发现。
      partiesInferred: p.inferred,
      aGroup: normalizeGroupRef(r.aGroup),   // deprecated：只留给"谁发起的"这类回读
      bGroup: normalizeGroupRef(r.bGroup),
      status: typeof r.status === 'string' ? r.status : '',
      messageCount: Math.max(0, Math.trunc(Number(r.messageCount)) || 0),
      lastAtMs: Number.isFinite(Number(r.lastAtMs)) ? Number(r.lastAtMs) : 0,
      // ── 归档态（第一百二十五批 C）：读服务端的 archiveState，**不在这里重算** ──
      // 后端有一处派生（thread_state）算这三种状态；前端拿两个时间戳自己再判一次，
      // 就是同一条规则的第二份实现，而两份实现迟早在某个边界上分岔（"待归档"到底
      // 是不是已经到期，恰恰是它们最容易分岔的地方）。缺席/不认得的值一律按 open
      // 待之：真闸在后端（归档后回帖 409），这里只是不说谎。
      archiveState: ARCHIVE_STATES.includes(r.archiveState) ? r.archiveState : 'open',
      archivePendingAtMs: Number.isFinite(Number(r.archivePendingAtMs)) ? Number(r.archivePendingAtMs) : 0,
      archivedAtMs: Number.isFinite(Number(r.archivedAtMs)) ? Number(r.archivedAtMs) : 0,
      // 这条提议什么时候自动落定。后端算好给的（窗口长度是服务端配置）；
      // null＝窗口被关掉了，即**永远不会**自动归档——0 在这里就是"没有到期时刻"。
      archiveDueAtMs: Number.isFinite(Number(r.archiveDueAtMs)) ? Number(r.archiveDueAtMs) : 0,
      archiveAuto: r.archiveAuto === true,     // 这次归档是走完窗口自动落的
      viaAgent: r.viaAgent === true,           // 线本身由 agent 开（凭据推导，同消息）
    });
  }
  return out;
}

// 当前该展示哪条线。wanted 仍在列表里就留住（重载列表不该把用户踢回第一条），
// 否则落到第一条；空列表返回空串（调用侧据此走"还没有线"的空态）。
// 与 reports.mjs 的 pickReportKey 同形——选中态做**派生**不做 effect 的理由
// 见那边头注 2（docHistory.mjs 付过的学费）。
export function pickThreadId(threads, wanted) {
  const list = Array.isArray(threads) ? threads : [];
  if (list.length === 0) return '';
  const w = wanted == null ? '' : String(wanted);
  if (w && list.some((t) => t.id === w)) return w;
  return list[0].id;
}

// 详情 → { thread, messages }。两种包裹形状都收（{thread:{…},messages:[…]} 或
// 平铺 {id,…,messages:[…]}）——合同注明"形状以实际返回为准"，收敛点只此一处，
// 起栈 curl 校准时改这里，页面不用动。
// 消息显式按 seq 升序（同 seq 按时间兜底成全序），理由见头注 3。
export function normalizeThreadDetail(data) {
  if (!data || typeof data !== 'object') return { thread: null, messages: [] };
  const raw = (data.thread && typeof data.thread === 'object') ? data.thread : data;
  const [t] = normalizeThreads({ threads: [raw] });
  const rows = Array.isArray(data.messages) ? data.messages
    : Array.isArray(raw.messages) ? raw.messages : [];
  const messages = rows
    .filter((m) => m && typeof m === 'object')
    .map((m) => ({
      seq: Math.max(0, Math.trunc(Number(m.seq)) || 0),
      body: typeof m.body === 'string' ? m.body : '',
      authorDisplay: typeof m.authorDisplay === 'string' ? m.authorDisplay : '',
      asGroup: typeof m.asGroup === 'string' ? m.asGroup.trim() : '',
      viaAgent: m.viaAgent === true,           // 只有真 true 才算 agent 写的（凭据推导）
      atMs: Number.isFinite(Number(m.atMs)) ? Number(m.atMs) : 0,
    }))
    .sort((a, b) => (a.seq - b.seq) || (a.atMs - b.atMs));
  return { thread: t ?? null, messages };
}

// 线的参与组（N 个，已在 normalize 时去重定序）。回帖的 asGroup 只能从这里选——
// 这是"代表组 ∈ 参与集"的唯一来源，徽标列表、选择器与校验共用它。
export function partyGroups(thread) {
  return Array.isArray(thread?.parties) ? thread.parties : [];
}

// slug → 线上那个参与组的对象（消息头上的代表组徽标要挂在参与组上，不是任意组）。
export function partyBySlug(thread, slug) {
  const s = typeof slug === 'string' ? slug : '';
  return partyGroups(thread).find((g) => g.slug === s) ?? null;
}

// 组引用 → 显示名。实际读面只随线带 slug（routes_threads 的 slug_of），双语名
// 要从会话上下文（/api/me 的 groups）按 slug 找回来；找不回（别组的线，普通成员
// 的 groups 里没有它）就如实显示 slug——slug 是发布目录的一段，可读性差但诚实，
// 编一个名字才是错的。ref 自带名字（对象形状）时作第二回落。
export function threadGroupLabel(ref, knownGroups, lang) {
  const slug = typeof ref === 'string' ? ref.trim() : (ref?.slug ?? '');
  if (!slug) return '';
  const known = (Array.isArray(knownGroups) ? knownGroups : [])
    .find((g) => g && g.slug === slug);
  return (known ? groupLabel(known, lang) : '')
    || (ref && typeof ref === 'object' ? groupLabel(ref, lang) : '')
    || slug;
}

// 我的组的 slug 集合（下面三处闸共用一个口径；散着写迟早漂开）。
function mySlugSet(myGroups) {
  return new Set((Array.isArray(myGroups) ? myGroups : [])
    .map((g) => (g && typeof g.slug === 'string' ? g.slug : ''))
    .filter(Boolean));
}

// 回帖可代表哪几个组、默认哪个、要不要锁死。
// **选项 = 参与集 ∩ 我的组**（口径沿用第一百零四批对抗审查坐实的那条，只把
// "线两端"换成"参与集"）：首版把两端原样端给选择器，非本端成员选上去必吃后端
// 403——界面递出一个注定失败的选项，等于替用户挖坑。管理员不需要特判：/api/me
// 对管理员回的就是全部活跃组，交集自然等于整个参与集。交集为空（有 liaison 权
// 但不在任何参与组里）⇒ options 空，调用方据此**整个不渲染写面**——合法性依然
// 由后端最终把守，但界面不再递刀。
//   · 恰一个合法组 ⇒ locked：没有第二个选择时给可切的选择器只会诱导误选。
export function defaultAsGroup(thread, myGroups) {
  const mine = mySlugSet(myGroups);
  const options = partyGroups(thread).filter((g) => mine.has(g.slug));
  if (options.length === 0) return { slug: '', locked: false, options };
  return { slug: options[0].slug, locked: options.length === 1, options };
}

// ── 追加参与方（第一百零六批 C1）────────────────────────────────────────────
// 入口显隐：canLiaison ∧ 我是**已在场**的一方。后端的闸是同一条（非在场者 403），
// 这里只是不递刀——一个点下去必红的按钮比没有按钮更伤。
export function canInviteParty(thread, myGroups, canLiaison) {
  if (!canLiaison) return false;
  if (thread?.status === 'closed') return false;   // closed 线连新参与方也不收（409）
  if (thread?.archiveState === 'archived') return false;   // 归档＝只读（第一百二十五批 C）
  const mine = mySlugSet(myGroups);
  return partyGroups(thread).some((g) => mine.has(g.slug));
}

// ── 归档三个按钮的显隐（第一百二十五批 C）──────────────────────────────────────
// 三条共用同一个"我够不够格动这条线"的前提：canLiaison ∧ 我是**已在场**的一方
// ——与后端 archive_precheck 的最后一道闸是同一条。抽成一只私有谓词而不是各写
// 一遍：三处各写一遍时，改闸只改了两处的表现，是"某个按钮点下去必红"。
function mayDecide(thread, myGroups, canLiaison) {
  if (!canLiaison) return false;
  const mine = mySlugSet(myGroups);
  return partyGroups(thread).some((g) => mine.has(g.slug));
}

// 发起归档：线还开着（没有在飞的提议、也没归档）。
// **不看 status**：一条 closed 的线照样该能归档——谈完了的线正是最该收进归档柜的。
export function canRequestArchive(thread, myGroups, canLiaison) {
  return mayDecide(thread, myGroups, canLiaison) && thread?.archiveState === 'open';
}

// 否决：有一个在飞的提议。任意参与方都可以，**不限于发起人**（裁决②的字面意思：
// "三日内任意参与方可取消"）——把它收窄成"只有发起人能撤回"会让否决权名存实亡。
export function canCancelArchive(thread, myGroups, canLiaison) {
  return mayDecide(thread, myGroups, canLiaison) && thread?.archiveState === 'archive_pending';
}

// 重开：已归档（裁决③）。
export function canReopenThread(thread, myGroups, canLiaison) {
  return mayDecide(thread, myGroups, canLiaison) && thread?.archiveState === 'archived';
}

// 回帖框显隐：canLiaison ∧ 线未 closed ∧ **未归档**。
// 判据搬进这里而不是留在 JSX 的三目里，是因为 `.jsx` 不进单测（SB-042）——
// 第一百零八批 R1 坐实过：判定写在 JSX 里，把它删掉全套测试仍然全绿。
// 待归档（archive_pending）**照常可以回帖**：那正是裁决④——发言即取消，
// 而一个在待归档时就把回帖框藏起来的界面，等于把"发言"这条否决路堵死了。
export function canReplyToThread(thread, canLiaison) {
  if (!canLiaison) return false;
  if (thread?.status === 'closed') return false;
  return thread?.archiveState !== 'archived';
}

// ── 归档三件的成功回执说哪一句 ──────────────────────────────────────────────
// 「已提议归档」那一句说的是**到期时刻**（服务端算好随行带回的 archiveDueAtMs），
// 而**不是**一个写死的天数：窗口长度是服务端配置（sweep.threadArchiveVetoDays），
// 写死的那一行在配置改成 7 的当天就成了假话，且没有任何东西会响。
// 窗口被关掉时后端给 null ⇒ 这条线**不会**自己完成，那是另一句话——同一句话配一个
// 空时刻会印成「无人取消的话它会在 — 自动归档」，一句读不懂的话。
// 取消/重开两句与时刻无关，原样透传。
export function archiveDoneKey(okKey, dueAtMs) {
  if (okKey !== 'threads.archive.requested') return okKey;
  const n = Number(dueAtMs);
  return Number.isFinite(n) && n > 0 ? okKey : 'threads.archive.requestedNever';
}

// ── 这一句回复顺手撤掉了归档提议吗（裁决④）───────────────────────────────────
// 后端在 reply 201 里特意带回动作**之后**的 archiveState，正是为了让调用方不必自己
// 推断"我这一句是不是刚好撤掉了一个提议"。前端此前一处都没读它：回帖成功只弹一句
// 「已回复」，而屏幕上那枚「待归档 · X 时刻自动归档」的徽标在下一次 reload 之后
// 悄悄不见了——用户既不知道自己撤了别人的提议，也无从判断那是不是个 bug。
// 判定放这里而不是 JSX 的三目里：`.jsx` 不进单测（SB-042，第一百零八批 R1 的账）。
// 方向保守：只有"回帖前确实待归档、回帖后服务端确实说 open"才算撤掉了——
// 服务端没回这个字段（老镜像/被中间层剥掉）时按"没撤"待之，宁可少说一句，
// 也不对着一条其实还待归档的线宣布"你已经取消了归档提议"。
export function replyCancelledArchive(before, after) {
  return before?.archiveState === 'archive_pending' && after?.archiveState === 'open';
}

// 铃铛给的那条链：`/threads?focus={id}`（notifTarget.mjs）。只认十进制数字串——
// 一个任意字符串进来没有害处，但收敛掉能让"这个参数是个线 id"成为读代码时看得出
// 的事（messages.mjs 的 focusIdFromSearch 同款，措辞一致是刻意的）。
export function focusIdFromSearch(search) {
  const v = readParam(search, 'focus');
  return v && /^\d+$/.test(v) ? v : null;
}

// 铃铛的「已归档」那条链带 `showArchived=1`：已归档的线默认不在列表里，不带它
// 的链接会跳到一个**看不见那条线**的页面，而那看起来像"这条线没了"。
// 只作**初值**：用户在页面上切了开关之后，地址栏不再是权威（否则每次切换都要改
// 地址栏，而返回键会把他弹回去）——与 MessagesPage 的 box 同款取舍。
export function showArchivedFromSearch(search) {
  const v = readParam(search, 'showArchived');
  return v === '1' || v === 'true';
}

function readParam(search, name) {
  const s = String(search ?? '');
  const q = s.startsWith('?') ? s.slice(1) : s;
  for (const part of q.split('&')) {
    if (!part) continue;
    const i = part.indexOf('=');
    if ((i < 0 ? part : part.slice(0, i)) !== name) continue;
    return i < 0 ? '' : decodeURIComponent(part.slice(i + 1).replace(/\+/g, ' '));
  }
  return null;
}

// 可邀请的组 = 候选全集 − 已在场。已在场的组再邀请一次是幂等的无害动作，
// 但摆在选单里会让人以为"它还没进来"——选单是一份关于现状的陈述。
export function inviteOptions(thread, options) {
  const here = new Set(partyGroups(thread).map((g) => g.slug));
  return (Array.isArray(options) ? options : []).filter((g) => g?.slug && !here.has(g.slug));
}

// 发起新线的「发起组」选项：**只有我的组**（管理员经 /api/me 天然全组）。
// 首版复用了下面 TO 侧的并集——把别的线端上见过的组也端进 FROM 选择器，
// 非成员选中即 403，同一类递刀（对抗审查坐实）。
export function fromGroupOptions(myGroups) {
  const out = [];
  const seen = new Set();
  for (const g of Array.isArray(myGroups) ? myGroups : []) {
    const n = normalizeGroupRef(g);
    if (!n || !n.slug || seen.has(n.slug)) continue;
    seen.add(n.slug);
    out.push(n);
  }
  return out;
}

// 发起新线的组选项：我的组 ∪ 列表里见过的参与组，按 slug 去重、我的组在前。
// 为什么要并上参与组：读面是全员全量的，列表里天然带着系统里活跃的组——
// 而 /api/me 的 groups 对普通成员只有自己那几个，对方组不在其中时"发起一条
// 到别的组的线"在页面上就无解。合法性仍由后端把守（无权 403）。
export function groupOptionsForNewThread(threads, myGroups) {
  const out = [];
  const seen = new Set();
  const push = (g) => {
    const n = normalizeGroupRef(g);
    if (!n || !n.slug || seen.has(n.slug)) return;
    seen.add(n.slug);
    out.push(n);
  };
  for (const g of Array.isArray(myGroups) ? myGroups : []) push(g);
  for (const t of Array.isArray(threads) ? threads : []) for (const g of partyGroups(t)) push(g);
  return out;
}

// 新线表单此刻缺什么（i18n 键；null = 可提交）。灰按钮必须说得出为什么灰
// （第八十五批建组表单立下的口径），所以这里返回的是"缺的那一条"而不是布尔。
// 检查顺序即用户补齐的自然顺序：主题 → 发起组 → 对象组 → 正文。
// toGroups 是**多选**（第一百零六批 C1）：三方对表不该被逼成两条线。
export function newThreadProblem({ subject, fromGroup, toGroups, body } = {}) {
  const tos = Array.isArray(toGroups) ? toGroups.filter(Boolean) : [];
  if (!(typeof subject === 'string' && subject.trim())) return 'threads.form.needSubject';
  if (!fromGroup) return 'threads.form.needFrom';
  if (tos.length === 0) return 'threads.form.needTo';
  // 发起组混进对象里：后端去重后可能只剩一个组（400），且用户多半是误选。
  if (tos.includes(fromGroup)) return 'threads.form.sameGroup';
  if (!(typeof body === 'string' && body.trim())) return 'threads.form.needBody';
  return null;
}

// 表单选择 → 发给后端的 parties。**发起组恒在第 0 位**：后端拿它当开场白的署名组，
// 也拿它判成员闸（把别人的组摆第一位＝替它说第一句话，必 403）。去重保序。
export function newThreadParties(fromGroup, toGroups) {
  const out = [];
  for (const s of [fromGroup, ...(Array.isArray(toGroups) ? toGroups : [])]) {
    if (typeof s === 'string' && s && !out.includes(s)) out.push(s);
  }
  return out;
}
